// ============================================
// LinkUserModal
// Liên kết hồ sơ khách với tài khoản đăng nhập (sheet 2 — mục #65)
// ============================================
//
// `Customer` (hồ sơ mua hàng, sinh ra ở quầy) và `User` (tài khoản đăng nhập,
// sinh ra khi khách cài app) là hai bảng tách rời. Nhân viên dùng màn này khi
// khách nói "tôi có app rồi" mà điểm tích lại nằm ở hồ sơ quầy.
//
// Tìm theo tên/email/SĐT qua GET /users/?role=CUSTOMER.
//
// Bản trước có thêm ô dán thẳng user ID làm đường dự phòng, vì `/users/` khi
// đó bị bộ lọc store-scope loại sạch tài khoản CUSTOMER với STORE_ADMIN (tài
// khoản khách không thuộc cửa hàng nào) — AI_ADMIN thì không dính. BE vá và
// deploy 04/09/2026, nên đã gỡ ô đó: bắt nhân viên quầy dán UUID là đường dùng
// một lần cho lúc kẹt, không phải giao diện để lại lâu dài.

import { useState } from 'react';
import type { User } from '@conai/shared-types';
import { useI18n } from '../../../i18n';
import { useUsers, useLinkCustomerUser } from '../../../hooks';
import { useDebounce } from '../../../hooks/useDebounce';

interface LinkUserModalProps {
  customerId: string;
  customerName: string;
  onClose: () => void;
}

export function LinkUserModal({ customerId, customerName, onClose }: LinkUserModalProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useUsers({
    role: 'CUSTOMER',
    search: debouncedSearch || undefined,
    page_size: 10,
  });
  const { mutate: linkUser, isPending } = useLinkCustomerUser();

  const users = ((data as { results?: User[] } | undefined)?.results ?? []) as User[];

  const submit = (userId: string) => {
    if (!userId.trim()) return;
    linkUser({ customerId, userId: userId.trim() }, { onSuccess: onClose });
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[520px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('linkUserTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{customerName}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('linkUserSearchLabel')}
            </label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('linkUserSearchHint')}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
            />
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200">
            {isLoading ? (
              <p className="px-3 py-4 text-center text-sm text-slate-500">{t('loading')}</p>
            ) : users.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-slate-500">{t('linkUserNoResults')}</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {users.map((u) => (
                  <li key={u.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{u.full_name}</p>
                      <p className="truncate text-xs text-slate-500">{u.email || u.phone || u.id}</p>
                    </div>
                    <button
                      onClick={() => submit(u.id)}
                      disabled={isPending}
                      className="shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
                    >
                      {t('linkUserAction')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>

        <div className="flex justify-end border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
